import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimBounceElastic } from '@readdy/anim/button/bounce-elastic/react';
import { createAddress, getAddresses, updateAddress } from '@/api';
import MapEditorModal from './MapEditorModal';
import { getAddressTypeIcon } from '@/utils/deliveryAddress';
import BazaarakMap, { KOLN_CENTER } from '@/components/map/BazaarakMap';

interface AddressItem {
  id: string;
  display: string;
  label: string;
  street?: string;
  city?: string;
  zipCode?: string;
  isDefault: boolean;
  latitude?: number;
  longitude?: number;
  type?: number;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    state_district?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

type LocationType = 0 | 1 | 2 | 3 | 4;

const LOCATION_TYPES: { value: LocationType; icon: string; labelKey: string }[] = [
  { value: 0, icon: 'ri-building-line', labelKey: 'apartment' },
  { value: 1, icon: 'ri-home-smile-line', labelKey: 'house' },
  { value: 2, icon: 'ri-briefcase-line', labelKey: 'office' },
  { value: 3, icon: 'ri-tree-line', labelKey: 'outdoor' },
  { value: 4, icon: 'ri-map-pin-line', labelKey: 'elsewhere' },
];

function formatShortAddress(result: NominatimResult): string {
  const a = result.address;
  const street = [a.road, a.house_number].filter(Boolean).join(' ');
  const city = a.city || a.town || a.village || '';
  return [street, city].filter(Boolean).join(', ');
}

function formatSearchResultTitle(result: NominatimResult): string {
  const a = result.address;
  const street = [a.road, a.house_number].filter(Boolean).join(' ');
  return street || result.display_name.split(',')[0] || '';
}

function formatSearchResultSubtitle(result: NominatimResult): string {
  const a = result.address;
  const city = a.city || a.town || a.village || a.state || a.state_district || '';
  const country = a.country || '';
  return [city, country].filter(Boolean).join(', ');
}

interface AddAddressModalProps {
  isDarkMode: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress?: (display: string, addrId?: number, addrLat?: number, addrLon?: number, addrType?: number) => void;
  onAddressSaved: (saved?: {
    id?: number;
    display: string;
    lat?: number | null;
    lon?: number | null;
    type?: number;
  }) => void;
  addresses?: AddressItem[];
  onAddressesChange?: (addresses: AddressItem[]) => void;
  token: string | null;
  selectedAddressId?: number | null;
}

export default function AddAddressModal({
  isDarkMode,
  isOpen,
  onClose,
  onSelectAddress,
  onAddressSaved,
  addresses = [],
  onAddressesChange,
  token,
  selectedAddressId,
}: AddAddressModalProps) {
  const { t } = useTranslation();

  // Mode: 'browse' = list/search view, 'add' = form view
  const [mode, setMode] = useState<'browse' | 'add'>('browse');

  // Browse mode state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);

  // Load addresses state
  const [loadAddrLoading, setLoadAddrLoading] = useState(false);
  const [loadAddrError, setLoadAddrError] = useState('');
  const fetchedRef = useRef(false);
  const onAddressesChangeRef = useRef(onAddressesChange);
  onAddressesChangeRef.current = onAddressesChange;
  const tRef = useRef(t);
  tRef.current = t;

  // Add mode state
  const [selectedResult, setSelectedResult] = useState<NominatimResult | null>(null);
  const [showMapEditor, setShowMapEditor] = useState(false);
  const [phone, setPhone] = useState('');
  const [locationType, setLocationType] = useState<LocationType | null>(null);
  const [doorbellName, setDoorbellName] = useState('');
  const [entrance, setEntrance] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [displayAddress, setDisplayAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [floor, setFloor] = useState('');
  const [hasElevator, setHasElevator] = useState(false);

  // Edit saved address state
  const [editingSavedAddr, setEditingSavedAddr] = useState<AddressItem | null>(null);
  const [showSavedMapEditor, setShowSavedMapEditor] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Accessibility: check prefers-reduced-motion
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setMode('browse');
      setSearchQuery('');
      setSearchResults([]);
      setHasSearched(false);
      setIsSearching(false);
      setSelectedResult(null);
      setPhone('');
      setLocationType(null);
      setDoorbellName('');
      setEntrance('');
      setAdditionalInfo('');
      setFloor('');
      setHasElevator(false);
      setError('');
      setPhoneError('');
      setAddressError('');
      setSaving(false);
      setLoadAddrLoading(false);
      setLoadAddrError('');
      fetchedRef.current = false;
    }
  }, [isOpen]);

  // Load saved addresses
  useEffect(() => {
    if (!isOpen || !token) {
      fetchedRef.current = false;
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAddrs = async () => {
      setLoadAddrLoading(true);
      setLoadAddrError('');
      try {
        const data = await getAddresses();
        if (Array.isArray(data)) {
          const mapped: AddressItem[] = data.map((addr: any) => {
            const parts = [addr.street, addr.city, addr.state, addr.zipCode || addr.zip, addr.country].filter(Boolean);
            return {
              id: addr.id,
              display: parts.join(', ') || addr.label || tRef.current('addresses.addressFallback'),
              label: addr.label || tRef.current('addresses.addressFallback'),
              street: addr.street || addr.label || '',
              city: addr.city || '',
              zipCode: addr.zipCode || addr.zip || '',
              isDefault: addr.isDefault || false,
              latitude: addr.latitude ?? null,
              longitude: addr.longitude ?? null,
              type: Number(addr.type ?? 0),
            };
          });
          onAddressesChangeRef.current?.(mapped);
        }
      } catch {
        setLoadAddrError(tRef.current('errors.loadFailed'));
      } finally { setLoadAddrLoading(false); }
    };
    fetchAddrs();
  }, [isOpen, token]);

  // Search
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setIsSearching(true);
    setHasSearched(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(query)}`,
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setSearchResults(data);
      } else {
        setSearchResults([]);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery, handleSearch]);

  // Actions
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
          );
          const data = await res.json();
          if (data && data.display_name) {
            const result: NominatimResult = {
              place_id: data.place_id,
              display_name: data.display_name,
              lat: String(position.coords.latitude),
              lon: String(position.coords.longitude),
              address: data.address || {},
            };
            onSelectAddress?.(formatShortAddress(result), undefined, position.coords.latitude, position.coords.longitude);
            onClose();
          }
        } catch { /* silent */ }
        finally { setGettingLocation(false); }
      },
      () => { setGettingLocation(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleGetCurrentLocationForAdd = () => {
    if (!navigator.geolocation) return;
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
          );
          const data = await res.json();
          if (data && data.display_name) {
            const result: NominatimResult = {
              place_id: data.place_id,
              display_name: data.display_name,
              lat: String(position.coords.latitude),
              lon: String(position.coords.longitude),
              address: data.address || {},
            };
            setSelectedResult(result);
            setSearchQuery(formatShortAddress(result));
            setSearchResults([]);
            setAddressError('');
          }
        } catch { /* silent */ }
        finally { setGettingLocation(false); }
      },
      () => { setGettingLocation(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSelectSaved = (address: AddressItem) => {
    onSelectAddress?.(address.display, address.id, address.latitude, address.longitude, address.type);
    onClose();
  };

  const handleSelectSearchResult = (result: NominatimResult) => {
    setSelectedResult(result);
    setSearchQuery(formatShortAddress(result));
    setSearchResults([]);
    setAddressError('');
    setMode('add');
  };

  const handleAddManually = () => {
    setSelectedResult(null);
    setSearchQuery('');
    setSearchResults([]);
    setAddressError('');
    setMode('add');
  };

  const handleBackToBrowse = () => {
    setMode('browse');
    setSelectedResult(null);
    setSearchQuery('');
    setSearchResults([]);
    setPhone('');
    setLocationType(null);
    setDoorbellName('');
    setEntrance('');
    setAdditionalInfo('');
    setDisplayAddress('');
    setFloor('');
    setHasElevator(false);
    setError('');
    setPhoneError('');
    setAddressError('');
  };

  const handleEditPin = () => {
    if (!selectedResult) return;
    setShowMapEditor(true);
  };

  const handleMapConfirm = (result: NominatimResult) => {
    setSelectedResult(result);
    setSearchQuery(formatShortAddress(result));
    setAddressError('');
  };

  // Sync displayAddress when selectedResult changes
  useEffect(() => {
    if (selectedResult) {
      setDisplayAddress(formatShortAddress(selectedResult));
    }
  }, [selectedResult]);

  const getLocationTypeIcon = (): string => {
    if (locationType === null) return 'ri-map-pin-line';
    return LOCATION_TYPES[locationType]?.icon || 'ri-map-pin-line';
  };

  const handleEditSavedAddress = (addr: AddressItem) => {
    setEditingSavedAddr(addr);
    setShowSavedMapEditor(true);
  };

  const handleSavedMapConfirm = async (result: NominatimResult) => {
    if (!editingSavedAddr) return;
    setEditSaving(true);
    try {
      const shortAddr = formatShortAddress(result);
      const a = result.address;
      const street = [a.road, a.house_number].filter(Boolean).join(' ') || shortAddr;
      const city = a.city || a.town || a.village || '';
      await updateAddress(editingSavedAddr.id, {
        label: editingSavedAddr.label,
        fullName: '',
        phone: '',
        street,
        city,
        state: a.state || a.state_district || '',
        zipCode: a.postcode || '',
        country: a.country || '',
        type: editingSavedAddr.type ?? 0,
        isDefault: editingSavedAddr.isDefault,
        latitude: parseFloat(result.lat) || null,
        longitude: parseFloat(result.lon) || null,
      });
      const data = await getAddresses();
      if (Array.isArray(data)) {
        const mapped: AddressItem[] = data.map((addr: any) => {
          const parts = [addr.street, addr.city, addr.state, addr.zipCode || addr.zip, addr.country].filter(Boolean);
          return {
            id: addr.id,
            display: parts.join(', ') || addr.label || t('addresses.addressFallback'),
            label: addr.label || t('addresses.addressFallback'),
            street: addr.street || addr.label || '',
            city: addr.city || '',
            zipCode: addr.zipCode || addr.zip || '',
            isDefault: addr.isDefault || false,
            latitude: addr.latitude ?? null,
            longitude: addr.longitude ?? null,
            type: Number(addr.type ?? 0),
          };
        });
        onAddressesChangeRef.current?.(mapped);
      }
      setShowSavedMapEditor(false);
      setEditingSavedAddr(null);
    } catch { /* silent */ }
    finally { setEditSaving(false); }
  };

  const validate = () => {
    let hasError = false;
    if (!selectedResult) {
      setAddressError(t('addresses.addressRequired') || 'Please select an address');
      hasError = true;
    }
    if (!phone.trim()) {
      setPhoneError(t('addresses.phoneRequired') || 'Phone number is required');
      hasError = true;
    }
    return !hasError;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!selectedResult) return;

    const addr = selectedResult.address;
    const street = [addr.road, addr.house_number].filter(Boolean).join(' ');
    const city = addr.city || addr.town || addr.village || '';
    const state = addr.state || addr.state_district || '';
    const label = locationType !== null
      ? t(`addresses.${LOCATION_TYPES[locationType].labelKey}`)
      : t('addresses.addressFallback');

    try {
      setSaving(true);
      setError('');
      const shortAddr = displayAddress.trim() || formatShortAddress(selectedResult);
      const created = await createAddress({
        label,
        fullName: doorbellName || '',
        phone: phone.trim(),
        street: street || shortAddr,
        city: city || '',
        state: state || '',
        zipCode: addr.postcode || '',
        country: addr.country || '',
        type: locationType ?? 0,
        isDefault: false,
        latitude: parseFloat(selectedResult.lat) || null,
        longitude: parseFloat(selectedResult.lon) || null,
      });
      onAddressSaved({
        id: created?.id,
        display: shortAddr,
        lat: parseFloat(selectedResult.lat) || null,
        lon: parseFloat(selectedResult.lon) || null,
        type: locationType ?? 0,
      });
      onSelectAddress?.(
        shortAddr,
        created?.id,
        parseFloat(selectedResult.lat),
        parseFloat(selectedResult.lon),
        locationType ?? 0,
      );
      onClose();
    } catch (err: any) {
      setError(err.message || t('errors.loadFailed'));
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const showingSearchResults = searchQuery.trim().length > 0 && mode === 'browse';

  const inputClasses = `w-full px-3 py-2 rounded-lg text-xs border outline-none transition-all duration-200 ${
    isDarkMode
      ? 'bg-[#2a2a2a] border-[#3a3a3a] focus:border-gold/30 text-stone-100 placeholder:text-stone-500'
      : 'bg-stone-50/60 border-stone-200/40 focus:border-gold/30 text-stone-900 placeholder:text-stone-400'
  }`;

  return (
    <div className={`fixed inset-0 z-[100] flex items-end justify-center ${mode === 'browse' ? 'pb-4' : ''}`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        layout
        className={`relative overflow-hidden flex flex-col ${
          mode === 'add'
            ? 'w-full h-screen max-h-screen rounded-none md:w-[480px] md:h-[85vh] md:max-h-[85vh] md:rounded-2xl'
            : 'w-[92%] max-w-md mx-auto max-h-[80vh] rounded-2xl shadow-2xl'
        } ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'}`}
      >
        {/* === BROWSE MODE === */}
        <AnimatePresence mode="wait">
          {mode === 'browse' && (
            <motion.div
              key="browse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col flex-1 min-h-0"
            >
              {/* Drag Handle */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-gold/60"></div>
              </div>

              {/* Title */}
              <div className="px-4 pt-2 pb-3 flex-shrink-0">
                <h3 className={`text-center text-base font-bold ${isDarkMode ? 'text-stone-100' : 'text-slate-800'}`}>
                  {t('addresses.yourAddresses') || 'Your Addresses'}
                </h3>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto overscroll-contain flex-1 min-h-0 px-4">
                {loadAddrLoading ? (
                  <div className="space-y-0">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={`py-3 border-b animate-pulse ${
                        isDarkMode ? 'border-stone-800' : 'border-stone-100'
                      }`}>
                        <div className={`h-3.5 rounded w-2/3 mb-1.5 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`}></div>
                        <div className={`h-2.5 rounded w-1/2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`}></div>
                      </div>
                    ))}
                  </div>
                ) : loadAddrError ? (
                  <div className="text-center py-6">
                    <p className={`text-sm ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>{loadAddrError}</p>
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-8">
                    <p className={`text-sm ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>
                      {t('addresses.noAddresses') || 'No saved addresses'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {addresses.map((addr, idx) => {
                      const isSelected = selectedAddressId === addr.id;
                      return (
                        <button
                          key={addr.id}
                          onClick={() => handleSelectSaved(addr)}
                          className={`w-full flex items-center gap-3 py-3.5 text-left cursor-pointer ${
                            idx < addresses.length - 1
                              ? isDarkMode ? 'border-b border-stone-800' : 'border-b border-stone-100'
                              : ''
                          }`}
                        >
                          {/* Icon */}
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <i className={`text-xl ${
                              isSelected
                                ? `${getAddressTypeIcon(addr.type, true)} text-gold`
                                : `${getAddressTypeIcon(addr.type, false)} ${isDarkMode ? 'text-stone-400' : 'text-slate-700'}`
                            }`}></i>
                          </div>

                          {/* Text */}
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-bold truncate ${
                              isSelected
                                ? 'text-gold'
                                : isDarkMode ? 'text-stone-100' : 'text-slate-800'
                            }`}>
                              {addr.street || addr.label}
                            </div>
                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>
                              {[addr.zipCode, addr.city].filter(Boolean).join(' ')}
                            </div>
                          </div>

                          {/* Edit */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditSavedAddress(addr); }}
                            className={`w-8 h-8 flex items-center justify-center flex-shrink-0 cursor-pointer ${
                              isSelected
                                ? 'text-gold hover:text-gold-dark'
                                : isDarkMode ? 'text-stone-500 hover:text-stone-300' : 'text-slate-500 hover:text-slate-700'
                            }`}
                            title={t('addresses.editPin') || 'Edit location'}
                          >
                            <i className="ri-pencil-line text-lg"></i>
                          </button>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer - Add Address Manually */}
              <div className={`px-4 py-4 flex-shrink-0 ${
                isDarkMode ? 'border-t border-stone-800' : 'border-t border-stone-100'
              }`}>
                <button
                  onClick={handleAddManually}
                  className="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark active:scale-[0.98]"
                >
                  <i className="ri-add-line text-sm"></i>
                  <span>{t('addresses.addAddress') || 'Add new address'}</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === ADD MODE === */}
        <AnimatePresence mode="wait">
          {mode === 'add' && (
            <motion.div
              key="add"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col flex-1 min-h-0"
            >
              {/* Header with Back */}
              <div className={`flex items-center gap-2 px-3 py-2 border-b flex-shrink-0 ${
                isDarkMode ? 'border-stone-800' : 'border-stone-100'
              }`}>
                <button
                  onClick={handleBackToBrowse}
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                    isDarkMode ? 'bg-stone-800 hover:bg-stone-700 text-stone-300' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
                  }`}
                >
                  <i className="ri-arrow-left-line text-xs"></i>
                </button>
                <h3 className={`text-xs font-bold ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>
                  {t('addresses.addNewAddress') || 'Add new address'}
                </h3>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
                <div className="px-3 py-2">
                  {/* Address Search (re-select) - hidden when address selected */}
                  <div className="mb-2">
                    {!selectedResult && (
                    <div className="relative">
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all duration-200 ${
                        selectedResult
                          ? isDarkMode ? 'bg-[#2a2a2a] border-gold/30' : 'bg-stone-50/80 border-gold/30'
                          : isDarkMode ? 'bg-[#2a2a2a] border-[#3a3a3a] focus-within:border-gold/25' : 'bg-stone-50/60 border-stone-200/40 focus-within:border-gold/25'
                      }`}>
                        <i className={`text-sm flex-shrink-0 ${selectedResult ? 'ri-map-pin-fill text-gold' : `ri-search-line ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}`}></i>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setSelectedResult(null);
                            setAddressError('');
                          }}
                          placeholder={t('addresses.searchPlaceholder') || 'Search for address...'}
                          disabled={!!selectedResult}
                          className={`flex-1 bg-transparent outline-none text-xs font-medium ${
                            isDarkMode ? 'text-stone-100 placeholder:text-stone-500' : 'text-stone-900 placeholder:text-stone-400'
                          } ${selectedResult ? 'opacity-80' : ''}`}
                        />
                        {(searchQuery || selectedResult) && (
                          <button
                            onClick={() => { setSearchQuery(''); setSearchResults([]); setSelectedResult(null); setAddressError(''); }}
                            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-stone-200/20 transition-colors"
                          >
                            <i className={`ri-close-circle-fill text-xs ${isDarkMode ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600'}`}></i>
                          </button>
                        )}
                      </div>
                      {addressError && (
                        <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>{addressError}
                        </p>
                      )}

                      {/* Use Current Location */}
                      {searchResults.length === 0 && !selectedResult && (
                        <button
                          onClick={handleGetCurrentLocationForAdd}
                          disabled={gettingLocation}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer mt-1.5 rounded-lg transition-colors duration-200 hover:bg-stone-100 dark:hover:bg-stone-800"
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-navigation-line text-sm text-gold"></i>
                          </div>
                          <div>
                            <div className={`text-xs font-semibold ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>
                              {t('home.useCurrentLocation') || 'Use my current location'}
                            </div>
                            <div className={`text-[10px] ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>
                              {t('home.locationPermissionNeeded') || 'Location permission required'}
                            </div>
                          </div>
                        </button>
                      )}

                      {/* Search Results Dropdown in add mode */}
                      <AnimatePresence>
                        {searchResults.length > 0 && !selectedResult && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.15 }}
                            className={`absolute left-0 right-0 top-full mt-1 rounded-lg border shadow-lg z-20 overflow-hidden ${
                              isDarkMode ? 'bg-[#1a1a1a] border-stone-800' : 'bg-white border-stone-200/40'
                            }`}
                          >
                            {searchResults.map((result, idx) => (
                              <button
                                key={result.place_id}
                                onClick={() => {
                                  setSelectedResult(result);
                                  setSearchQuery(formatShortAddress(result));
                                  setSearchResults([]);
                                  setAddressError('');
                                }}
                                className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors cursor-pointer ${
                                  isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-50'
                                } ${idx !== searchResults.length - 1 ? (isDarkMode ? 'border-b border-stone-800' : 'border-b border-stone-100') : ''}`}
                              >
                                <div>
                                  <div className={`text-xs font-semibold ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>
                                    {formatSearchResultTitle(result)}
                                  </div>
                                  <div className={`text-[10px] ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>
                                    {formatSearchResultSubtitle(result)}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    )}

                    {/* Mini Map */}
                    <AnimatePresence>
                      {selectedResult && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="mt-2"
                        >
                          <div className={`rounded-xl border p-3 space-y-2 mx-2.5 ${
                            isDarkMode ? 'bg-[#222] border-stone-800' : 'bg-stone-50/80 border-stone-200/40'
                          }`}>
                            <div className="relative">
                        <div className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-md text-[10px] ${
                          isDarkMode ? 'bg-gold/10 border border-gold/10' : 'bg-gold/5 border border-gold/10'
                        }`}>
                          <i className={`${getLocationTypeIcon()} text-gold mt-px flex-shrink-0 text-[10px]`}></i>
                          <input
                            type="text"
                            value={displayAddress}
                            onChange={(e) => setDisplayAddress(e.target.value)}
                            placeholder={t('addresses.addressPlaceholder') || 'آدرس را وارد کنید...'}
                            className={`flex-1 bg-transparent outline-none font-medium leading-relaxed text-[10px] ${
                              isDarkMode ? 'text-stone-300 placeholder:text-stone-600' : 'text-stone-700 placeholder:text-stone-400'
                            }`}
                          />
                          <button
                            onClick={() => { setSelectedResult(null); setSearchQuery(''); setSearchResults([]); setAddressError(''); setDisplayAddress(''); }}
                            className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-stone-200/20 dark:hover:bg-stone-700/30 rounded-full transition-colors"
                            title={t('common.clear') || 'پاک کردن'}
                          >
                            <i className={`ri-close-circle-fill text-[10px] ${isDarkMode ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600'}`}></i>
                          </button>
                        </div>
                      </div>

                            <div className="relative rounded-lg overflow-hidden border border-stone-200/30">
                              <div className="w-full h-[140px] relative">
                                {selectedResult && (
                                  <BazaarakMap
                                    className="w-full h-full pointer-events-none"
                                    centerLon={Number(selectedResult.lon)}
                                    centerLat={Number(selectedResult.lat)}
                                    zoom={15}
                                    showControls={false}
                                    interactive={false}
                                  />
                                )}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <div className="w-8 h-8 rounded-full bg-dark-900/90 flex items-center justify-center shadow-lg shadow-black/30 -mt-4">
                                    <i className="ri-map-pin-fill text-white text-sm"></i>
                                  </div>
                                </div>
                                <button
                                  onClick={handleEditPin}
                                  className="absolute bottom-1.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold bg-white/90 text-gold border border-gold/20 shadow-sm hover:shadow-md transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
                                >
                                  <i className="ri-map-pin-line text-[10px]"></i>
                                  {t('addresses.editPin') || 'Edit on map'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>


                  {/* Lieferdetails Section */}
                  <AnimatePresence>
                    {selectedResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05, duration: 0.2 }}
                      >
                        <div className={`rounded-xl border p-3.5 space-y-2 mx-2.5 ${
                          isDarkMode ? 'bg-[#222] border-stone-800' : 'bg-stone-50/80 border-stone-200/40'
                        }`}>

                        {/* Location Type */}
                        <div>
                          <p className={`text-[10px] font-medium mb-2 ${isDarkMode ? 'text-stone-300' : 'text-stone-600'}`}>
                            {t('addresses.selectLocationType') || 'نوع مکان را انتخاب کنید'}
                          </p>
                          <div className="overflow-x-auto -mx-3 px-3 pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <div className="flex gap-3 w-max">
                              {LOCATION_TYPES.map((lt) => {
                                const isActive = locationType === lt.value;
                                const btn = (
                                  <button
                                    key={lt.value}
                                    onClick={() => setLocationType(isActive ? null : lt.value)}
                                    className={`flex flex-col items-center justify-center w-[140px] h-[120px] rounded-lg border transition-all duration-200 cursor-pointer flex-shrink-0 ${
                                      isActive
                                        ? 'border-gold/40 bg-gold/10 text-gold'
                                        : isDarkMode
                                          ? 'border-stone-800 bg-[#222] text-stone-400 hover:border-stone-700'
                                          : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                                    }`}
                                  >
                                    <i className={`${lt.icon} text-2xl mb-1`}></i>
                                    <span className="text-sm font-medium leading-tight text-center px-1">{t(`addresses.${lt.labelKey}`)}</span>
                                  </button>
                                );
                                if (prefersReducedMotion) return <div key={lt.value} className="flex-shrink-0">{btn}</div>;
                                return (
                                  <AnimBounceElastic key={lt.value} duration={650}>
                                    {btn}
                                  </AnimBounceElastic>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Fields Based on Location Type */}

                        {/* Phone — always shown */}
                        <div>
                          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all duration-200 ${
                            phoneError
                              ? 'border-red-400/40'
                              : isDarkMode ? 'bg-[#2a2a2a] border-[#3a3a3a] focus-within:border-gold/25' : 'bg-stone-50/60 border-stone-200/40 focus-within:border-gold/25'
                          }`}>
                            <i className={`ri-phone-line text-[10px] ${isDarkMode ? 'text-stone-400' : 'text-stone-400'}`}></i>
                            <input
                              type="tel"
                              value={phone}
                              onChange={(e) => { setPhone(e.target.value); setPhoneError(''); }}
                              placeholder={t('addresses.phonePlaceholder') || '۰۹۱۲۳۴۵۶۷۸۹'}
                              className="flex-1 bg-transparent outline-none text-[11px] font-medium text-stone-900 placeholder:text-stone-400 dark:text-stone-100 dark:placeholder:text-stone-500"
                              dir="ltr"
                            />
                          </div>
                          {phoneError && (
                            <p className="text-[10px] text-red-400 mt-0.5 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>{phoneError}
                            </p>
                          )}
                        </div>

                        {/* Name — shown for Apartment, House, Office */}
                        {(locationType === null || locationType === 0 || locationType === 1 || locationType === 2) && (
                          <div>
                            <input
                              type="text"
                              value={doorbellName}
                              onChange={(e) => setDoorbellName(e.target.value)}
                              placeholder={t('addresses.doorbellNamePlaceholder') || 'مثلاً "علی احمدی"'}
                              className={`${inputClasses} text-[11px]`}
                            />
                          </div>
                        )}

                        {/* Entrance + Floor — 2-col for Apartment & Office */}
                        {(locationType === null || locationType === 0 || locationType === 2) && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input
                                type="text"
                                value={entrance}
                                onChange={(e) => setEntrance(e.target.value)}
                                placeholder={t('addresses.entrancePlaceholder') || 'مثلاً "ورودی B"'}
                                className={`${inputClasses} text-[11px]`}
                              />
                            </div>
                            <div>
                              <input
                                type="text"
                                value={floor}
                                onChange={(e) => setFloor(e.target.value)}
                                placeholder={t('addresses.floorPlaceholder') || 'مثلاً "۳"'}
                                className={`${inputClasses} text-[11px]`}
                              />
                            </div>
                          </div>
                        )}

                        {/* Entrance only — full width for House */}
                        {locationType === 1 && (
                          <div>
                            <input
                              type="text"
                              value={entrance}
                              onChange={(e) => setEntrance(e.target.value)}
                              placeholder={t('addresses.entrancePlaceholder') || 'مثلاً "ورودی B"'}
                              className={`${inputClasses} text-[11px]`}
                            />
                          </div>
                        )}

                        {/* Directions for Courier — shown for all */}
                        <div>
                          <textarea
                            value={additionalInfo}
                            onChange={(e) => setAdditionalInfo(e.target.value)}
                            placeholder={t('addresses.additionalInfoPlaceholder') || 'راهنمایی برای پیک...'}
                            rows={2}
                            maxLength={500}
                            className={`${inputClasses} resize-none text-[11px]`}
                          />
                          <div className={`text-[9px] text-right mt-0.5 ${isDarkMode ? 'text-stone-600' : 'text-stone-400'}`}>
                            {additionalInfo.length}/500
                          </div>
                        </div>

                        {/* Elevator Toggle — only for Apartment & Office */}
                        {(locationType === null || locationType === 0 || locationType === 2) && (
                          <div className={`flex items-center justify-between py-0.5 ${
                            isDarkMode ? 'text-stone-300' : 'text-stone-700'
                          }`}>
                            <span className="text-[11px] font-medium">{t('addresses.elevator') || 'آسانسور دارد؟'}</span>
                            <button
                              type="button"
                              onClick={() => setHasElevator(!hasElevator)}
                              className={`relative w-9 h-5 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
                                hasElevator ? 'bg-gold' : isDarkMode ? 'bg-stone-700' : 'bg-stone-300'
                              }`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                hasElevator ? 'translate-x-4' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className={`mt-3 p-2 rounded-lg flex items-center gap-2 border text-xs ${
                          isDarkMode ? 'bg-red-500/15 border-red-500/25' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <i className="ri-error-warning-line text-red-400 text-xs"></i>
                        <span className="text-red-600 dark:text-red-300 flex-1">{error}</span>
                        <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 cursor-pointer">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Footer - Save */}
              <div className={`px-3 py-2 border-t flex-shrink-0 ${
                isDarkMode ? 'border-stone-800' : 'border-stone-100'
              }`}>
                <button
                style={{"margin":"10px" , "width":"calc(100% - 20px)"}}
                
                  onClick={handleSave}
                  disabled={!selectedResult || saving}
                  className={`w-full py-2.5 rounded-xl font-semibold text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    !selectedResult || saving
                      ? isDarkMode ? 'bg-stone-800 text-stone-500 cursor-not-allowed' : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-gold-dark to-gold-light text-white hover:scale-[1.01] shadow-md shadow-gold/15'
                  }`}
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-1">
                      <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                      {t('common.loading') || 'Loading...'}
                    </span>
                  ) : (
                    t('addresses.saveAddress') || 'Save address'
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Map Editor for new address */}
      <MapEditorModal
        isDarkMode={isDarkMode}
        isOpen={showMapEditor}
        onClose={() => setShowMapEditor(false)}
        initialLat={selectedResult?.lat || String(KOLN_CENTER.lat)}
        initialLon={selectedResult?.lon || String(KOLN_CENTER.lon)}
        initialAddress={selectedResult?.display_name || ''}
        addressType={locationType}
        onConfirm={handleMapConfirm}
      />

      {/* Map Editor for saved address */}
      <MapEditorModal
        isDarkMode={isDarkMode}
        isOpen={showSavedMapEditor}
        onClose={() => { setShowSavedMapEditor(false); setEditingSavedAddr(null); }}
        initialLat={editingSavedAddr?.latitude ? String(editingSavedAddr.latitude) : '52.52'}
        initialLon={editingSavedAddr?.longitude ? String(editingSavedAddr.longitude) : '13.405'}
        initialAddress={editingSavedAddr?.display || ''}
        addressType={editingSavedAddr?.type ?? 0}
        onConfirm={handleSavedMapConfirm}
      />
    </div>
  );
}